// src/components/navigation/ArrivalScreen.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  DoorFront as DoorIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';

/**
 * Arrival screen shown when destination is reached
 * @param {Object} props
 * @param {Object} props.destination - Destination POI data with name and metadata
 * @param {string} props.destinationPhotoUrl - Photo URL of the destination location
 * @param {string} props.destinationPhotoCaption - Caption for the destination photo
 * @param {string} props.openDoorUrl - URL to trigger door unlock (from location config or POI metadata)
 * @param {function} props.onClose - Callback when navigation is closed
 */
const ArrivalScreen = ({
  destination,
  destinationPhotoUrl,
  destinationPhotoCaption,
  openDoorUrl,
  onClose,
}) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [doorLoading, setDoorLoading] = useState(false);
  const [doorError, setDoorError] = useState(null);
  const [doorSuccess, setDoorSuccess] = useState(false);

  // Check for openDoorUrl from props, destination config (location.data), or destination metadata
  // location.data is an array of config objects: [{name, value, type, ...}, ...]
  const getConfigValue = (config, fieldName) => {
    if (!config) return null;
    // If config is an array, find the object with matching name and return its value
    if (Array.isArray(config)) {
      const field = config.find(f => f.name === fieldName);
      return field?.value || null;
    }
    // If config is a plain object, access the property directly
    return config[fieldName] || null;
  };

  const doorUrl = openDoorUrl
    || getConfigValue(destination?.config, 'openDoorUrl')
    || destination?.metadata?.openDoorUrl
    || destination?.openDoorUrl;

  const handleOpenDoor = async () => {
    if (!doorUrl) return;

    setDoorLoading(true);
    setDoorError(null);
    setDoorSuccess(false);

    try {
      const response = await fetch(doorUrl, {
        method: 'GET',
        mode: 'no-cors', // Door systems often don't support CORS
      });
      // With no-cors, we can't read the response, but the request is sent
      setDoorSuccess(true);
      setTimeout(() => setDoorSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to open door:', error);
      setDoorError('Failed to send door unlock signal');
    } finally {
      setDoorLoading(false);
    }
  };

  return (
    <>
      <Card
        elevation={4}
        sx={{
          maxWidth: 600,
          width: '100%',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2,
            }}
          />

          <Typography variant="h5" gutterBottom fontWeight="bold">
            You've Arrived!
          </Typography>

          {destination && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Welcome to {destination.name}
            </Typography>
          )}

          {/* Door status messages */}
          {doorError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {doorError}
            </Alert>
          )}
          {doorSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Door unlock signal sent successfully
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Open Door Button - only shown if openDoorUrl is configured in location custom fields */}
            {doorUrl && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={doorLoading ? <CircularProgress size={20} color="inherit" /> : <DoorIcon />}
                onClick={handleOpenDoor}
                disabled={doorLoading}
              >
                {doorLoading ? 'Opening...' : 'Open Door'}
              </Button>
            )}

            <Button
              variant={doorUrl ? 'outlined' : 'contained'}
              size={doorUrl ? 'small' : 'large'}
              onClick={onClose}
            >
              Close Navigation
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Image Zoom Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={destinationPhotoUrl}
            alt={destination?.name || 'Destination'}
            style={{ width: '100%', display: 'block' }}
          />
          {destinationPhotoCaption && (
            <Typography variant="body2" sx={{ p: 2, backgroundColor: 'background.paper' }}>
              {destinationPhotoCaption}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ArrivalScreen;
